import { Component, EventEmitter, Input, Output, ViewChild, ElementRef, ChangeDetectorRef, OnInit } from '@angular/core';
import { HttpClient, HttpEventType, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { GenericUploadTranslationService } from './generic-upload-translation.service';
import { GenericUploadTranslations, GenericUploadTranslationInput } from './generic-upload-translations.interface';

export enum UploadFileState {
  Pending = 'pending',
  Uploading = 'uploading',
  Completed = 'completed',
  Failed = 'failed',
}

export class UploadFile {
  id: string;
  file: File;
  name: string;
  size: number;
  state: UploadFileState = UploadFileState.Pending;
  progress: number = 0;
  response?: any;
  error?: any;
  errorMessage?: string;

  constructor(file: File) {
    this.id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    this.file = file;
    this.name = file.name;
    this.size = file.size;
  }

  get sizeFormatted(): string {
    const bytes = this.size;
    if (bytes === 0) return '0';
    const k = 1024;
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + '';
  }

  getSizeFormattedWithUnit(units: GenericUploadTranslations['units']): string {
    const bytes = this.size;
    if (bytes === 0) return `0 ${units.bytes}`;
    const k = 1024;
    const sizes = [units.bytes, units.kb, units.mb, units.gb];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  get extension(): string {
    const parts = this.name.split('.');
    return parts.length > 1 ? '.' + parts[parts.length - 1].toLowerCase() : '';
  }
}

export interface GenericUploadSuccessEvent {
  file: UploadFile;
  response?: HttpResponse<any>;
}

export interface GenericUploadErrorEvent {
  file: UploadFile;
  response?: HttpErrorResponse;
  status?: number;
}

export interface UploadRestrictions {
  allowedExtensions?: string[];
  maxFileSize?: number;
  minFileSize?: number;
}

@Component({
  selector: 'app-generic-upload',
  templateUrl: './generic-upload.component.html',
  styleUrls: ['./generic-upload.component.scss'],
})
export class GenericUploadComponent implements OnInit {
  @ViewChild('fileInput', { static: false }) fileInput: ElementRef<HTMLInputElement>;

  /**
   * The URL to which the files will be uploaded
   */
  @Input() uploadUrl: string;

  /**
   * File restrictions (extensions, size limits)
   */
  @Input() restrictions: UploadRestrictions = {};

  /**
   * Whether multiple files can be selected
   */
  @Input() multiple: boolean = false;

  /**
   * Whether files should be uploaded automatically after selection
   */
  @Input() autoUpload: boolean = false;

  /**
   * Whether to send credentials with the request
   */
  @Input() withCredentials: boolean = false;

  /**
   * Whether the component is disabled
   */
  @Input() disabled: boolean = false;

  /**
   * The form field name used for the file in the multipart request. Defaults to 'file'.
   */
  @Input() fileFieldName: string = 'file';

  /**
   * Static additional form fields appended to every upload request.
   * Merged before formDataFactory, so factory values take precedence on the same key.
   */
  @Input() additionalFormData?: Record<string, string | Blob>;

  /**
   * Per-file factory that returns additional form fields for a given file.
   * Called for each upload and takes precedence over additionalFormData.
   */
  @Input() formDataFactory?: (file: UploadFile) => Record<string, string | Blob>;

  /**
   * Whether to show the retry button for failed uploads
   */
  @Input() showRetryButton: boolean = true;

  /**
   * Whether to show the remove button for files
   */
  @Input() showRemoveButton: boolean = true;

  /**
   * Optional ID for the file input element (for proper label connection)
   */
  @Input() inputId?: string;

  /**
   * Optional translations for the component
   */
  @Input() translations?: GenericUploadTranslationInput;

  /**
   * One-way binding to provide file list to hosting component
   */
  @Input() files: UploadFile[] = [];

  /**
   * Emitted when file(s) upload successfully
   */
  @Output() uploadSuccess = new EventEmitter<GenericUploadSuccessEvent>();

  /**
   * Emitted when file(s) upload fails
   */
  @Output() uploadError = new EventEmitter<GenericUploadErrorEvent>();

  /**
   * Emitted when files are selected
   */
  @Output() filesSelected = new EventEmitter<UploadFile[]>();

  /**
   * Emitted when files list changes
   */
  @Output() filesChange = new EventEmitter<UploadFile[]>();

  public isDragOver: boolean = false;
  public UploadFileState = UploadFileState;
  public resolvedTranslations: GenericUploadTranslations;
  
  /**
   * Internal array for invalid files (validation errors) - only for display, not exposed via binding
   */
  private invalidFiles: UploadFile[] = [];
  
  /**
   * Combined files for display (valid + invalid)
   */
  public get allFilesForDisplay(): UploadFile[] {
    return [...this.files, ...this.invalidFiles];
  }

  constructor(
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private translationService: GenericUploadTranslationService
  ) {
    // Initialize translations with defaults + global
    this.resolvedTranslations = this.translationService.getResolvedTranslations();
  }

  ngOnInit(): void {
    // Update translations with local translations if provided
    this.resolvedTranslations = this.translationService.getResolvedTranslations(this.translations);
  }

  public onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFiles(Array.from(input.files));
    }
  }

  public onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.disabled) {
      this.isDragOver = true;
    }
  }

  public onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  public onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    if (this.disabled) return;

    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      this.handleFiles(Array.from(event.dataTransfer.files));
    }
  }

  public selectFiles(): void {
    if (!this.disabled && this.fileInput) {
      this.fileInput.nativeElement.click();
    }
  }

  public onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.selectFiles();
    }
  }

  private handleFiles(fileList: File[]): void {
    const validFiles: UploadFile[] = [];
    const newInvalidFiles: UploadFile[] = [];

    for (const file of fileList) {
      const uploadFile = new UploadFile(file);
      const validationError = this.validateFile(uploadFile);

      if (validationError) {
        uploadFile.state = UploadFileState.Failed;
        uploadFile.error = validationError;
        uploadFile.errorMessage = validationError;
        newInvalidFiles.push(uploadFile);
        this.uploadError.emit({
          file: uploadFile
        });
      } else {
        validFiles.push(uploadFile);
      }
    }

    // Handle valid files
    if (validFiles.length > 0) {
      if (!this.multiple) {
        // Replace existing files if single mode
        this.files = validFiles.slice(0, 1);
        this.invalidFiles = []; // Clear invalid files in single mode
      } else {
        // Add to existing files if multiple mode
        this.files = [...this.files, ...validFiles];
      }

      this.filesChange.emit(this.files);
      this.filesSelected.emit(validFiles);

      if (this.autoUpload) {
        this.uploadFiles();
      }
    }

    // Handle invalid files (only for display)
    if (newInvalidFiles.length > 0) {
      if (!this.multiple) {
        // Replace invalid files in single mode
        this.invalidFiles = newInvalidFiles.slice(0, 1);
      } else {
        // Add to existing invalid files in multiple mode
        this.invalidFiles = [...this.invalidFiles, ...newInvalidFiles];
      }
    }

    // Reset file input
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }

    this.cdr.markForCheck();
  }

  private validateFile(uploadFile: UploadFile): string | null {
    // Check file extension
    if (this.restrictions.allowedExtensions && this.restrictions.allowedExtensions.length > 0) {
      const allowed = this.restrictions.allowedExtensions.some(
        ext => uploadFile.extension.toLowerCase() === ext.toLowerCase()
      );
      if (!allowed) {
        return this.translationService.getFromResolved(
          'errors.fileTypeNotAllowed',
          this.resolvedTranslations,
          { 
            extension: uploadFile.extension,
            allowedTypes: this.restrictions.allowedExtensions.join(', ')
          }
        );
      }
    }

    // Check minimum file size
    if (this.restrictions.minFileSize && uploadFile.size < this.restrictions.minFileSize) {
      return this.translationService.getFromResolved(
        'errors.fileTooSmall',
        this.resolvedTranslations,
        { minSize: this.formatBytes(this.restrictions.minFileSize) }
      );
    }

    // Check maximum file size
    if (this.restrictions.maxFileSize && uploadFile.size > this.restrictions.maxFileSize) {
      return this.translationService.getFromResolved(
        'errors.fileTooLarge',
        this.resolvedTranslations,
        { maxSize: this.formatBytes(this.restrictions.maxFileSize) }
      );
    }

    return null;
  }

  /**
   * Programmatically trigger file upload for all pending files
   * @param retryFailed - If true, also retry failed uploads
   */
  public uploadFiles(retryFailed: boolean = false): void {
    const filesToUpload = retryFailed
      ? this.files.filter(f => f.state === UploadFileState.Pending || f.state === UploadFileState.Failed)
      : this.files.filter(f => f.state === UploadFileState.Pending);
    
    filesToUpload.forEach(file => {
      // Reset failed files to pending state before uploading
      if (file.state === UploadFileState.Failed) {
        file.state = UploadFileState.Pending;
        file.error = undefined;
        file.errorMessage = undefined;
        file.progress = 0;
      }
      this.uploadFile(file);
    });
  }

  /**
   * Upload a specific file
   */
  public uploadFile(uploadFile: UploadFile): void {
    if (!this.uploadUrl || uploadFile.state !== UploadFileState.Pending) {
      return;
    }

    uploadFile.state = UploadFileState.Uploading;
    uploadFile.progress = 0;
    this.cdr.markForCheck();

    const formData = new FormData();
    formData.append(this.fileFieldName, uploadFile.file, uploadFile.name);
    const staticFields = this.additionalFormData ?? {};
    const dynamicFields = this.formDataFactory ? this.formDataFactory(uploadFile) : {};
    const extraFields = { ...staticFields, ...dynamicFields };
    for (const [key, value] of Object.entries(extraFields)) {
      formData.append(key, value);
    }

    this.http.post(this.uploadUrl, formData, {
      reportProgress: true,
      observe: 'events',
      withCredentials: this.withCredentials,
    }).subscribe({
      next: (event) => {
        if (event.type === HttpEventType.UploadProgress) {
          if (event.total) {
            uploadFile.progress = Math.round((100 * event.loaded) / event.total);
            this.cdr.markForCheck();
          }
        } else if (event.type === HttpEventType.Response) {
          uploadFile.state = UploadFileState.Completed;
          uploadFile.progress = 100;
          uploadFile.response = event.body;
          this.filesChange.emit(this.files);
          this.uploadSuccess.emit({
            file: uploadFile,
            response: event,
          });
          this.cdr.markForCheck();
        }
      },
      error: (error: HttpErrorResponse) => {
        uploadFile.state = UploadFileState.Failed;
        uploadFile.error = error.error || error.message;
        uploadFile.errorMessage = typeof uploadFile.error === 'string' 
          ? uploadFile.error 
          : this.resolvedTranslations.errors.uploadFailed;
        this.filesChange.emit(this.files);
        this.uploadError.emit({
          file: uploadFile,
          response: error,
          status: error.status,
        });
        this.cdr.markForCheck();
      }
    });
  }

  /**
   * Remove a file from the list
   */
  public removeFile(uploadFile: UploadFile): void {
    // Remove from valid files
    const wasInValidFiles = this.files.some(f => f.id === uploadFile.id);
    this.files = this.files.filter(f => f.id !== uploadFile.id);
    
    // Remove from invalid files
    this.invalidFiles = this.invalidFiles.filter(f => f.id !== uploadFile.id);
    
    // Only emit change if it was a valid file
    if (wasInValidFiles) {
      this.filesChange.emit(this.files);
    }
    
    this.cdr.markForCheck();
  }

  /**
   * Clear all files
   */
  public clearFiles(): void {
    this.files = [];
    this.invalidFiles = [];
    this.filesChange.emit(this.files);
    this.cdr.markForCheck();
  }

  /**
   * Retry uploading a failed file
   */
  public retryFile(uploadFile: UploadFile): void {
    if (uploadFile.state === UploadFileState.Failed) {
      uploadFile.state = UploadFileState.Pending;
      uploadFile.error = undefined;
      uploadFile.errorMessage = undefined;
      uploadFile.progress = 0;
      this.uploadFile(uploadFile);
    }
  }

  /**
   * Get the number of files (valid files only)
   */
  public getFileCount(): number {
    return this.files.length;
  }

  /**
   * Check if there are files ready to upload
   */
  public hasFiles(): boolean {
    return this.files.length > 0;
  }

  /**
   * Check if there are files pending upload
   */
  public hasPendingFiles(): boolean {
    return this.files.some(f => f.state === UploadFileState.Pending);
  }

  /**
   * Get files by state
   */
  public getFilesByState(state: UploadFileState): UploadFile[] {
    return this.files.filter(f => f.state === state);
  }

  /**
   * Format bytes for display in template
   */
  public formatBytes(bytes: number): string {
    if (bytes === 0) return `0 ${this.resolvedTranslations.units.bytes}`;
    const k = 1024;
    const sizes = [
      this.resolvedTranslations.units.bytes,
      this.resolvedTranslations.units.kb,
      this.resolvedTranslations.units.mb,
      this.resolvedTranslations.units.gb
    ];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  /**
   * Get localized label for file state (for screen readers)
   */
  public getFileStateLabel(state: UploadFileState): string {
    switch (state) {
      case UploadFileState.Pending:
        return this.resolvedTranslations.states.pending;
      case UploadFileState.Uploading:
        return this.resolvedTranslations.states.uploading;
      case UploadFileState.Completed:
        return this.resolvedTranslations.states.completed;
      case UploadFileState.Failed:
        return this.resolvedTranslations.states.failed;
      default:
        return '';
    }
  }
}
